import { ColDef, ICellRendererParams } from 'ag-grid-community';

import { udcMultiSelectWithSearchFilter } from '../../../../../../UiComponentsLibrary/UDC';

import { UDCActionCellRenderer } from '../../../../../../Shared/components/UDC';

import { TABLE_COLUMNS } from '../../../config/columnConfig';
import { FORM_FIELDS } from '../../../../CreateBooking/constants/FormFieldsConstants';
import { ActionIconConfig } from '../../../config/actionButtonConfig';
import { FILTERABLE_COLUMNS } from '../constants/filterableColumns';
import { QueueSheetManagementRows } from '../../../interface/QueueSheetInterface';
import SlotNamesCellRenderer from './SlotNamesCellRenderer';
import GtmStatusCellRenderer from './GtmStatusCellRenderer';

export const getColumnVisibilityConfig = () => [
  { colId: 'action', alwaysHidden: false, alwaysDisplayed: true, hideWhenResetToDefault: false },
  { colId: FORM_FIELDS.ENTRY_ID, alwaysHidden: false, alwaysDisplayed: true, hideWhenResetToDefault: false },
  { colId: FORM_FIELDS.BRAND, alwaysHidden: false, alwaysDisplayed: true, hideWhenResetToDefault: false },
  { colId: FORM_FIELDS.AE_NAME, alwaysHidden: false, alwaysDisplayed: false, hideWhenResetToDefault: false },
  { colId: FORM_FIELDS.AM_NAME, alwaysHidden: false, alwaysDisplayed: false, hideWhenResetToDefault: false },
  { colId: FORM_FIELDS.CATEGORY, alwaysHidden: false, alwaysDisplayed: false, hideWhenResetToDefault: false },
  { colId: FORM_FIELDS.TYPE_OF_PURCHASE, alwaysHidden: false, alwaysDisplayed: false, hideWhenResetToDefault: false },
  { colId: FORM_FIELDS.EXACT_BILLING_VALUE, alwaysHidden: false, alwaysDisplayed: false, hideWhenResetToDefault: false },
  { colId: FORM_FIELDS.SF_OPPORTUNITY_ID, alwaysHidden: false, alwaysDisplayed: false, hideWhenResetToDefault: false },
  { colId: FORM_FIELDS.RO, alwaysHidden: false, alwaysDisplayed: false, hideWhenResetToDefault: false },
  { colId: FORM_FIELDS.BRAND_CONFIRMATION, alwaysHidden: false, alwaysDisplayed: false, hideWhenResetToDefault: false },
  { colId: FORM_FIELDS.RODEO_AD_NAME, alwaysHidden: false, alwaysDisplayed: false, hideWhenResetToDefault: false },
  { colId: FORM_FIELDS.GTM_STATUS, alwaysHidden: false, alwaysDisplayed: false, hideWhenResetToDefault: false },
  { colId: FORM_FIELDS.COMMENTS_BY_POC, alwaysHidden: false, alwaysDisplayed: false, hideWhenResetToDefault: true },
  { colId: FORM_FIELDS.CREATIVES, alwaysHidden: false, alwaysDisplayed: false, hideWhenResetToDefault: false },
  { colId: FORM_FIELDS.MODIFIED_AT, alwaysHidden: false, alwaysDisplayed: false, hideWhenResetToDefault: false },
  { colId: FORM_FIELDS.MODIFIED_BY, alwaysHidden: false, alwaysDisplayed: false, hideWhenResetToDefault: true },
  { colId: FORM_FIELDS.SLOT_NAMES, alwaysHidden: false, alwaysDisplayed: false, hideWhenResetToDefault: false },
];

export const getColumnManagerPresets = (t: (key: string) => string) => [
  {
    presetId: 'ALL_COLUMNS',
    label: t('all-columns'),
    disabledStatus: { disabled: false, message: null },
    columns: getColumnVisibilityConfig().map(col => ({ ...col, alwaysDisplayed: true })),
  },
];

interface GetColumnDefsParams {
  t: (key: string) => string;
  actionButtonConfig: ActionIconConfig[];
  brands: string[];
  getDisplayValue: (colKey: string, value: unknown) => unknown;
  // When true (super admin, active view), prepend a checkbox column for multi-select bulk actions.
  enableSelection?: boolean;
}

const SELECT_COLUMN_WIDTH = 50;

const getSelectColumn = (): ColDef<QueueSheetManagementRows> => ({
  colId: 'select',
  headerName: '',
  width: SELECT_COLUMN_WIDTH,
  minWidth: SELECT_COLUMN_WIDTH,
  maxWidth: SELECT_COLUMN_WIDTH,
  pinned: 'left' as const,
  sortable: false,
  filter: false,
  resizable: false,
  checkboxSelection: true,
  headerCheckboxSelection: true,
  headerCheckboxSelectionFilteredOnly: true,
});

const TOOLTIP_COLUMNS: string[] = [FORM_FIELDS.SLOT_NAMES, FORM_FIELDS.COMMENTS_BY_POC];

const getColumnFilter = (colKey: string, brands: string[]) => {
  const isFilterable =
    (colKey === FILTERABLE_COLUMNS.BRAND && brands?.length > 0) ||
    colKey === FILTERABLE_COLUMNS.SLOT_NAMES ||
    colKey === FILTERABLE_COLUMNS.GTM_STATUS;
  return isFilterable ? udcMultiSelectWithSearchFilter : false;
};

const slotNamesComparator = (cellValue: string[], filterModel: { values: { value: string }[] }) => {
  if (!filterModel?.values?.length) return true;
  if (!cellValue || !Array.isArray(cellValue) || cellValue.length === 0) return false;
  return filterModel.values.some(filterItem => cellValue.includes(filterItem.value));
};

const getColumnFilterParams = (colKey: string, brands: string[], t: (key: string) => string) => {
  if (colKey === FILTERABLE_COLUMNS.BRAND && brands?.length > 0) {
    return { buttons: [t('apply'), t('reset')] };
  }
  if (colKey === FILTERABLE_COLUMNS.SLOT_NAMES) {
    return { buttons: [t('apply'), t('reset')], comparator: slotNamesComparator };
  }
  if (colKey === FILTERABLE_COLUMNS.GTM_STATUS) {
    return { buttons: [t('apply'), t('reset')] };
  }
  return undefined;
};

export const getColumnDefs = ({ t, actionButtonConfig, brands, getDisplayValue, enableSelection = false }: GetColumnDefsParams): ColDef<QueueSheetManagementRows>[] => [
  ...(enableSelection ? [getSelectColumn()] : []),
  {
    colId: 'action',
    headerName: t('table-header-action'),
    width: 200,
    minWidth: 180,
    pinned: 'left' as const,
    sortable: false,
    filter: false,
    cellRenderer: UDCActionCellRenderer({ actionButtonConfig, t }),
  },
  ...TABLE_COLUMNS.map((col) => ({
    colId: col.key,
    headerName: t(col.header),
    field: col.key,
    width: 150,
    minWidth: 120,
    sortable: true,
    filter: getColumnFilter(col.key, brands),
    filterParams: getColumnFilterParams(col.key, brands, t),
    ...(col.key === FILTERABLE_COLUMNS.SLOT_NAMES && {
      filterValueGetter: (params: { data: QueueSheetManagementRows }) => {
        return params.data?.slotPackageNames || [];
      },
    }),
    floatingFilter: false,
    ...(['ro', 'brandConfirmation', 'creatives', 'modifiedAt'].includes(col.key) && {
      cellRenderer: (p: ICellRendererParams<QueueSheetManagementRows>) => getDisplayValue(col.key, p.value),
    }),
    ...(col.key === FORM_FIELDS.GTM_STATUS && {
      cellRenderer: GtmStatusCellRenderer,
    }),
    ...(TOOLTIP_COLUMNS.includes(col.key) && {
      cellRenderer: SlotNamesCellRenderer,
    }),
    ...(col.key === FORM_FIELDS.SLOT_NAMES && {
      cellClass: 'ag-cell-tooltip-column',
      autoHeight: true,
    }),
  })),
];
